if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this._onChange = this._onChange.bind(this);
        this._onClickCapture = this._onClickCapture.bind(this);

        // ✅ Guards against async race conditions
        this._reqToken = 0;
        this._skuAbort = null;
        this._priceAbort = null;
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('click', this._onClickCapture, true);
        this.addEventListener('change', this._onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClickCapture, true);
        this.removeEventListener('change', this._onChange);

        // cleanup
        this._skuAbort?.abort();
        this._priceAbort?.abort();
      }

      _onClickCapture(event) {
        const label = event.target.closest('label');
        let input = null;

        if (label && label.htmlFor) {
          input = this.querySelector(`#${CSS.escape(label.htmlFor)}`);
        } else {
          input = event.target.closest('input[type="radio"], input[type="checkbox"]');
        }

        if (!input) return;

        const isDisabled =
          input.disabled ||
          input.classList.contains('disabled') ||
          input.classList.contains('visually-disabled');

        if (!isDisabled) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
      }

      _onChange(event) {
        const target = this.getInputForEventTarget(event.target);

        if (target?.classList?.contains('disabled') || target?.classList?.contains('visually-disabled')) {
          event.preventDefault();
          return;
        }

        this.updateSelectedSwatchValue(event);

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: { event, target, selectedOptionValues: this.selectedOptionValues },
        });

        // Let theme update hidden variant input[name="id"] first
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.updateUrlVariantParam();

            // ✅ New token per change
            const token = ++this._reqToken;

            // Update SKU + price, but only if they’re still latest
            this.updateSkuFromVariantEndpoint(token);
            this.updatePriceFromSection(token);
          });
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');

          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');

          if (!selectedDropdownSwatchValue) return;

          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest('.product-form__input')?.querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      getCurrentVariantId() {
        const form = this.closest('form') || document.querySelector('form[action^="/cart/add"]');
        const idInput = form?.querySelector('input[name="id"]');
        return idInput?.value ? String(idInput.value) : null;
      }

      updateUrlVariantParam() {
        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      getClosestSectionEl() {
        return (
          this.closest('[data-section-id]') ||
          this.closest('.shopify-section') ||
          document.querySelector('[data-section-id]') ||
          document.querySelector('.shopify-section')
        );
      }

      // ✅ SKU: cancel previous request + only apply if still latest
      async updateSkuFromVariantEndpoint(token) {
        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        // Cancel any previous SKU fetch
        this._skuAbort?.abort();
        this._skuAbort = new AbortController();

        const sectionEl = this.getClosestSectionEl();
        const skuEl =
          (sectionEl && sectionEl.querySelector('[data-product-sku]')) ||
          document.querySelector('[data-product-sku]');

        if (!skuEl) return;

        try {
          const res = await fetch(`/variants/${encodeURIComponent(variantId)}.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: this._skuAbort.signal,
          });
          if (!res.ok) return;

          const variant = await res.json();

          // ✅ Guard 1: only latest token can write
          if (token !== this._reqToken) return;

          // ✅ Guard 2: only write if current selected variant is still the same
          if (this.getCurrentVariantId() !== variantId) return;

          skuEl.textContent = variant?.sku || '';
        } catch (e) {
          // Ignore abort errors
        }
      }

      // ✅ Price: same protection (optional but recommended)
      async updatePriceFromSection(token) {
        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        // Cancel any previous price fetch
        this._priceAbort?.abort();
        this._priceAbort = new AbortController();

        const sectionEl = this.getClosestSectionEl();
        if (!sectionEl) return;

        const sectionId =
          sectionEl.dataset?.sectionId ||
          (sectionEl.id ? sectionEl.id.replace('shopify-section-', '') : null);

        if (!sectionId) return;

        const url = `${window.location.pathname}?variant=${encodeURIComponent(
          variantId
        )}&section_id=${encodeURIComponent(sectionId)}`;

        try {
          const res = await fetch(url, { credentials: 'same-origin', signal: this._priceAbort.signal });
          const html = await res.text();

          // ✅ Guard 1: only latest token can write
          if (token !== this._reqToken) return;

          // ✅ Guard 2: only write if current selected variant is still the same
          if (this.getCurrentVariantId() !== variantId) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');

          const currentPrice = sectionEl.querySelector('.f-price');
          const nextPrice = doc.querySelector('.f-price');

          if (currentPrice && nextPrice) {
            currentPrice.innerHTML = nextPrice.innerHTML;
          }

          const bssPriceWrap = sectionEl.querySelector('[bss-b2b-variant-id]');
          if (bssPriceWrap) {
            bssPriceWrap.setAttribute('bss-b2b-variant-id', variantId);
          }
        } catch (e) {
          // Ignore abort errors
        }
      }
    }
  );
}
